'use client'
// =============================================================================
// HudProgress / HudRing — barre et anneau lumineux.
// Règle : `value` est une fraction RÉELLE (0–1) calculée à partir de données de l'API, ou `null` quand la donnée n'existe pas.
// `null` n'est jamais dessiné comme 0 % : la piste reste vide, pointillée, avec la mention fournie (« non renseigné »…).
// =============================================================================

const clamp = (v: number) => Math.max(0, Math.min(1, v))
const pct = (v: number) => `${Math.round(clamp(v) * 100)} %`

export function HudProgress({ value, label, detail, tone, unavailable = 'non renseigné' }: {
  value: number | null
  label?: string
  /** Texte réel à côté de la barre (ex. « 3 / 8 tâches »). */
  detail?: string
  tone?: 'ok' | 'warn' | 'bad'
  unavailable?: string
}) {
  const has = value !== null && Number.isFinite(value)
  return (
    <div className={`hud-prog${has ? '' : ' na'}${tone ? ` ${tone}` : ''}`}>
      {(label || detail !== undefined || !has) && (
        <div className="hud-prog-top"><span>{label}</span><span>{has ? (detail ?? pct(value as number)) : unavailable}</span></div>
      )}
      <div className="hud-prog-track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100}
        aria-valuenow={has ? Math.round(clamp(value as number) * 100) : undefined} aria-valuetext={has ? pct(value as number) : unavailable}>
        {has && <i style={{ width: pct(value as number) }} />}
      </div>
    </div>
  )
}

export function HudRing({ value, size = 64, label, unavailable = '—' }: { value: number | null; size?: number; label?: string; unavailable?: string }) {
  const has = value !== null && Number.isFinite(value)
  const r = (size - 8) / 2, c = 2 * Math.PI * r
  return (
    <div className={`hud-ring${has ? '' : ' na'}`} style={{ width: size, height: size }} role="img" aria-label={label ? `${label} : ${has ? pct(value as number) : 'non renseigné'}` : undefined}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} className="track" />
        {has && <circle cx={size / 2} cy={size / 2} r={r} className="arc" strokeDasharray={`${c * clamp(value as number)} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
      </svg>
      <span>{has ? pct(value as number) : unavailable}</span>
    </div>
  )
}
